// src/components/challenge/challenge.component.js
import React, { Component } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import styled, { createGlobalStyle } from "styled-components";
import ReactGA from "react-ga4";

const catOptions = {
  1: {
    name: "Healthy Living",
    icon: "fitness",
    iconLibrary: "ionicons",
    iconColor: "#27AE60",
    bgcolor: "#D4AC0D",
    img: "cats/cat1a.png",
  },
  2: {
    name: "Break Habits",
    icon: "person-circle-outline",
    iconLibrary: "ionicons",
    iconColor: "#E74C3C",
    bgcolor: "#3F888F",
    img: "cats/cat2c.png",
  },
  3: {
    name: "Career Growth",
    icon: "suitcase",
    iconLibrary: "FontAwesome5",
    iconColor: "#2C3E50",
    bgcolor: "#FF847C",
    img: "cats/cat3a.png",
  },
  4: {
    name: "Smart Money",
    icon: "finance",
    iconLibrary: "MaterialCommunityIcons",
    iconColor: "#1ABC9C",
    bgcolor: "#FFA500",
    img: "cats/cat4a.png",
  },
  5: {
    name: "Lifelong Learning",
    icon: "book-education",
    iconLibrary: "MaterialCommunityIcons",
    iconColor: "#F4D03F",
    bgcolor: "#B565A7",
    img: "cats/cat5a.png",
  },
  6: {
    name: "Explore More",
    icon: "travel-explore",
    iconLibrary: "MaterialIcons",
    iconColor: "#2980B9",
    bgcolor: "#FF7F11",
    img: "cats/cat6a.png",
  },
  7: {
    name: "Family Life",
    icon: "heart-circle-outline",
    iconLibrary: "ionicons",
    iconColor: "#EC7063",
    bgcolor: "#967BB6",
    img: "cats/cat7a.png",
  },
  8: {
    name: "Enjoy Life",
    icon: "clipboard-outline",
    iconLibrary: "ionicons",
    iconColor: "#F7DC6F",
    bgcolor: "#9B2335",
    img: "cats/cat8a.png",
  },
  9: {
    name: "Social Impact",
    icon: "social-skillshare",
    iconLibrary: "Foundation",
    iconColor: "#2E86C1",
    bgcolor: "#88B04B",
    img: "cats/cat9a.png",
  },
  10: {
    name: "Inner Peace",
    icon: "hand-peace",
    iconLibrary: "MaterialCommunityIcons",
    iconColor: "#A9DFBF",
    bgcolor: "#6B5B95",
    img: "cats/cat10a.png",
  },
  11: {
    name: "Get Creative",
    icon: "heart-circle-outline",
    iconLibrary: "ionicons",
    iconColor: "#F39C12",
    bgcolor: "#6B5B95",
    img: "cats/cat11a.png",
  },
  12: {
    name: "Learn Language",
    icon: "language",
    iconLibrary: "ionicons",
    iconColor: "#1F618D",
    action: "custom",
    bgcolor: "#f55442",
    img: "cats/cat12a.png",
  },
  13: {
    name: "Mental Wellness",
    icon: "brain",
    iconLibrary: "MaterialCommunityIcons",
    iconColor: "#D35400",
    bgcolor: "#6B5B95",
    img: "cats/cat13a.png",
  },
  14: {
    name: "Stay Focused",
    icon: "center-focus-strong",
    iconLibrary: "MaterialIcons",
    iconColor: "#52BE80",
    bgcolor: "#6B5B95",
    img: "cats/cat14a.png",
  },
  15: {
    name: "Build Discipline",
    icon: "timetable",
    iconLibrary: "MaterialCommunityIcons",
    iconColor: "#8E44AD",
    action: "custom",
    bgcolor: "#f55442",
    img: "cats/cat15a.png",
  },
};

const GlobalStyle = createGlobalStyle`
  html, body, #root {
    padding: 0;
    margin: 0;
    min-height: 100%;
    width: 100%;
  }

  body {
    overflow-x: hidden;
    overflow-y: auto;
    align-items: center;
    font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    background: linear-gradient(135deg, #192334 0%, #202c3f 50%, #151d2b 100%);
  }

  * {
    box-sizing: border-box;
  }

  .p-challenge {
    word-break: break-word;
    overflow-wrap: break-word;
  }
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #192334 0%, #202c3f 50%, #151d2b 100%);
`;

const CenteredContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 820px;
  padding: 16px 0 28px 0;
`;

const ChallengeCard = styled.div`
  background: rgba(5, 10, 18, 0.88);
  backdrop-filter: blur(8px);
  border-radius: 18px;
  padding: 18px;
  margin: 14px;
  width: calc(100% - 28px);
  max-width: 620px;
  color: white;
  box-shadow: 0 16px 50px rgba(0, 0, 0, 0.34);
  border: 1px solid rgba(255, 255, 255, 0.06);
`;

const ChallengeTitle = styled.h1`
  font-size: 20px;
  font-weight: 800;
  margin: 0 0 8px 0;
  text-align: center;
  line-height: 1.2;
`;

const HeroSubheadline = styled.div`
  font-size: 14px;
  opacity: 0.9;
  text-align: center;
  line-height: 1.5;
  margin-bottom: 12px;
`;

const PillsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 12px 0;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  background: rgba(29, 155, 240, 0.14);
  color: #d9efff;
  border: 1px solid rgba(29, 155, 240, 0.32);
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
`;

const ChallengeDescription = styled.p`
  font-size: 16px;
  line-height: 1.7;
  margin: 16px 0 0 0;
  color: rgba(255, 255, 255, 0.94);
`;

const JoinButton = styled.button`
  background-color: #007df4;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 14px 18px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  width: 100%;
  margin-top: 6px;
  transition: all 0.18s ease;

  &:hover {
    background-color: #0068cc;
    transform: translateY(-1px);
  }
`;

class Challenge extends Component {
  constructor(props) {
    super(props);

    const challengeId = this.props.match.params.id;

    this.state = {
      challenge: {
        id: challengeId || "default",
        title: "30-Day Fitness Challenge",
        description:
          "Join our 30-day fitness challenge to transform your body and mind. This challenge includes daily workouts, nutrition tips, and community support.",
        imageUrl: "https://www.bealyo.com/images/prebealyoshare.png",
        startDate: "2025-01-01",
        endDate: "2025-01-30",
        participants: 1245,
        inviteId: "",
      },
      loading: true,
      error: null,
    };
  }

  componentDidMount() {
    const challengeId = this.props.match.params.id;

    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
    });

    axios
      .get(process.env.REACT_APP_API_URL + "goals/" + challengeId)
      .then((response) => {
        const goalData = response.data;

        const imageUrl =
          goalData.goal_imageurl && goalData.goal_imageurl !== ""
            ? process.env.REACT_APP_IMG_FOLDER + goalData.goal_imageurl
            : process.env.REACT_APP_IMG_FOLDER_FILES +
              catOptions[goalData.goal_catid || 1].img;

        this.setState({
          challenge: {
            id: goalData._id,
            title: goalData.goal_title,
            description: goalData.goal_desc,
            imageUrl,
            startDate: goalData.goal_start_date,
            endDate: goalData.goal_duedate,
            participants: goalData.num_users || 0,
            inviteId: goalData.goal_inviteid || "",
            taskCount: Array.isArray(goalData.tasks_docs)
              ? goalData.tasks_docs.length
              : 0,
            categoryName:
              catOptions[goalData.goal_catid || 1]?.name || "Challenge",
          },
          loading: false,
        });
      })
      .catch((error) => {
        console.error("Error fetching challenge:", error);
        this.setState({
          error: "Failed to load challenge details",
          loading: false,
        });
      });
  }

  render() {
    const { challenge, loading, error } = this.state;
    const shareUrl = `https://www.bealyo.com/challenge/${challenge.id}`;

    const ogTitle = challenge.title || "Join a Bealyo Challenge";
    const ogDescription =
      challenge.description ||
      "Be part of a community-driven challenge on Bealyo. Android Open Testing is live.";

    const peopleText =
      challenge.participants === 0
        ? "Be the first to take it on"
        : `${challenge.participants} people have already taken it on`;

    return (
      <PageContainer>
        <Helmet>
          <title>{challenge.title} | Bealyo</title>
          <meta name="description" content={challenge.description} />

          <meta property="al:android:package" content="com.bealyo.bealyoapp" />
          <meta
            property="al:android:url"
            content={`bealyo-app://challenge/${challenge.id}`}
          />
          <meta property="al:android:app_name" content="Bealyo" />

          <meta
            property="al:ios:url"
            content={`bealyo-app://challenge/${challenge.id}`}
          />
          <meta property="al:ios:app_store_id" content="YOUR_APP_STORE_ID" />
          <meta property="al:ios:app_name" content="Bealyo" />

          <meta property="og:type" content="website" />
          <meta property="og:url" content={shareUrl} />
          <meta property="og:title" content={ogTitle} />
          <meta property="og:description" content={ogDescription} />
          <meta property="og:image" content={challenge.imageUrl} />

          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content={shareUrl} />
          <meta property="twitter:title" content={ogTitle} />
          <meta property="twitter:description" content={ogDescription} />
          <meta property="twitter:image" content={challenge.imageUrl} />
        </Helmet>

        <GlobalStyle />

        <CenteredContainer>
          <ChallengeCard>
            {loading ? (
              <div style={{ padding: "30px 0", textAlign: "center" }}>
                Loading challenge...
              </div>
            ) : error ? (
              <div style={{ padding: "30px 0", textAlign: "center" }}>
                {error}
              </div>
            ) : (
              <>
                <ChallengeTitle>{challenge.title}</ChallengeTitle>

                <HeroSubheadline>
                  Start your own round on Bealyo — solo or with friends.
                </HeroSubheadline>

                <PillsRow>
                  <Pill>🔥 Start today</Pill>
                  <Pill>✨ Make it yours</Pill>
                  <Pill>👥 Do it with friends</Pill>
                </PillsRow>

                <div style={{ margin: "12px 0 8px 0" }}>
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "230px",
                      borderRadius: "14px",
                      overflow: "hidden",
                      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.25)",
                    }}
                  >
                    <img
                      src={challenge.imageUrl}
                      alt={challenge.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />

                    <div
                      style={{
                        position: "absolute",
                        top: "12px",
                        left: "12px",
                        background: "rgba(0,0,0,0.38)",
                        border: "1px solid rgba(255,255,255,0.10)",
                        borderRadius: "12px",
                        padding: "7px 10px",
                        backdropFilter: "blur(6px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src="https://www.bealyo.com/images/logo/logo.png"
                        alt="Bealyo"
                        style={{
                          height: "22px",
                          width: "auto",
                          display: "block",
                        }}
                      />
                    </div>

                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: "58%",
                        background:
                          "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.72) 100%)",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        bottom: "12px",
                        left: "14px",
                        right: "14px",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "15px",
                        lineHeight: 1.35,
                      }}
                    >
                      One day at a time. Every step counts.
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    fontSize: "13px",
                    lineHeight: 1.55,
                    color: "rgba(255,255,255,0.76)",
                    marginTop: "8px",
                    marginBottom: "2px",
                  }}
                >
                  {peopleText}
                </div>

                <ChallengeDescription>
                  {challenge.description}
                </ChallengeDescription>

                <div
                  style={{
                    margin: "16px 0 14px 0",
                    fontSize: "14px",
                    lineHeight: 1.65,
                    color: "rgba(255,255,255,0.86)",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.06)",
                    borderRadius: "14px",
                    padding: "14px 14px",
                  }}
                >
                  Start whenever you want. Go solo or invite friends for extra
                  support and accountability.
                </div>

                {!!challenge.inviteId && (
                  <div
                    style={{
                      margin: "0 0 14px 0",
                      fontSize: "14px",
                      lineHeight: 1.65,
                      color: "rgba(255,255,255,0.92)",
                      background: "rgba(0,125,244,0.10)",
                      border: "1px solid rgba(0,125,244,0.28)",
                      borderRadius: "14px",
                      padding: "14px 14px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.6px",
                        opacity: 0.78,
                        marginBottom: "6px",
                      }}
                    >
                      Challenge code
                    </div>

                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: 800,
                        letterSpacing: "1.2px",
                        color: "#ffffff",
                        marginBottom: "8px",
                        wordBreak: "break-word",
                      }}
                    >
                      {challenge.inviteId}
                    </div>

                    <div
                      style={{
                        fontSize: "13px",
                        color: "rgba(255,255,255,0.82)",
                        lineHeight: 1.55,
                      }}
                    >
                      Install Bealyo, then paste this code in the app to open
                      this challenge directly.
                    </div>
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    marginTop: "4px",
                  }}
                >
                  <span
                    style={{
                      alignSelf: "flex-start",
                      background: "rgba(29, 155, 240, 0.14)",
                      color: "#d9efff",
                      border: "1px solid rgba(29, 155, 240, 0.32)",
                      borderRadius: "999px",
                      padding: "5px 10px",
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    Android available now
                  </span>

                  <JoinButton
                    onClick={() =>
                      window.open(
                        "https://play.google.com/store/apps/details?id=com.bealyo.bealyoapp",
                        "_blank",
                      )
                    }
                  >
                    Start this challenge on Bealyo
                  </JoinButton>

                  <div
                    style={{
                      fontSize: "12px",
                      opacity: 0.85,
                      textAlign: "center",
                      lineHeight: 1.5,
                    }}
                  >
                    Install the app and paste the challenge code to continue
                  </div>
                </div>
              </>
            )}
          </ChallengeCard>
        </CenteredContainer>
      </PageContainer>
    );
  }
}

export default Challenge;
